import { useEffect, useState } from 'react';
import { below } from './breakpoints';

export type SideNavVariant = 'drawer' | 'rail' | 'expanded';

const DRAWER_QUERY = below('md');
const RAIL_QUERY = below('lg');

/**
 * Which of the shell's three responsive SideNav presentations applies:
 *
 *   drawer   <672px (sm)      — hidden by default, opened by the header hamburger
 *   rail     672–1055px (md)  — Carbon's icon rail, always visible, no hamburger
 *   expanded >=1056px (lg)    — persistently expanded with labels, no hamburger
 *
 * `above('lg')` is not used directly: with two `below()` checks, the
 * matchMedia stub in vitest.setup.ts (always `matches: false`) falls through
 * to 'expanded' by default — the same "assume desktop" convention useIsMobile
 * and ThemeContext already rely on for JSDOM.
 */
export function useSideNavVariant(): SideNavVariant {
  const [variant, setVariant] = useState<SideNavVariant>(resolveVariant);

  useEffect(() => {
    if (typeof window === 'undefined' || window.matchMedia === undefined) return;
    const drawerQuery = window.matchMedia(DRAWER_QUERY);
    const railQuery = window.matchMedia(RAIL_QUERY);
    const update = (): void => setVariant(resolveVariant());
    update();
    drawerQuery.addEventListener('change', update);
    railQuery.addEventListener('change', update);
    return () => {
      drawerQuery.removeEventListener('change', update);
      railQuery.removeEventListener('change', update);
    };
  }, []);

  return variant;
}

function resolveVariant(): SideNavVariant {
  if (typeof window === 'undefined' || window.matchMedia === undefined) return 'expanded';
  if (window.matchMedia(DRAWER_QUERY).matches) return 'drawer';
  if (window.matchMedia(RAIL_QUERY).matches) return 'rail';
  return 'expanded';
}
